
import { useState, useEffect, useRef } from 'react';
import { collection, onSnapshot, query, orderBy, QueryConstraint } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Hook to sync a Firestore collection under the current user's document.
 * Path: /users/{userId}/{collectionName}
 */
const EMPTY_CONSTRAINTS: any[] = [];

import { useAuth } from '../components/contexts/AuthContext';

export const useFirestoreCollection = <T extends { id: string }>(
    collectionName: string,
    initialData: T[] = [],
    constraints: QueryConstraint[] = EMPTY_CONSTRAINTS,
    enabled: boolean = true,
    transform?: (doc: T) => Promise<T> | T
): [T[], boolean] => {
    const { user } = useAuth();
    const userId = user?.uid || null;
    const [data, setData] = useState<T[]>(initialData);
    const [loading, setLoading] = useState(enabled);

    // Track active query parameters in state to detect changes during render
    const [prevParams, setPrevParams] = useState(() => ({
        userId,
        collectionName,
        enabled,
        constraints: [...constraints]
    }));

    const paramsChanged =
        prevParams.userId !== userId ||
        prevParams.collectionName !== collectionName ||
        prevParams.enabled !== enabled ||
        prevParams.constraints.length !== constraints.length ||
        !prevParams.constraints.every((c, i) => c === constraints[i]);

    // Ref to cache untransformed items so we can re-transform them if the transform function changes
    const rawItemsRef = useRef<T[]>([]);

    if (paramsChanged) {
        setPrevParams({
            userId,
            collectionName,
            enabled,
            constraints: [...constraints]
        });
        setData(initialData);
        setLoading(enabled);
        rawItemsRef.current = [];
    }

    // Ref for transform function so subscription doesn't restart when transform reference changes
    const transformRef = useRef(transform);
    useEffect(() => {
        transformRef.current = transform;
    }, [transform]);

    // Re-run transform if transform changes and we have raw items cached
    useEffect(() => {
        if (!userId || !enabled || rawItemsRef.current.length === 0 || !transform) {
            return;
        }

        let isMounted = true;
        const retransform = async () => {
            try {
                const finalItems = await Promise.all(
                    rawItemsRef.current.map(item => transform(item))
                );
                if (isMounted) {
                    setData(prev => {
                        const isDifferent = finalItems.length !== prev.length ||
                            finalItems.some((item, index) => {
                                const existing = prev[index];
                                if (!existing) return true;
                                return (item as any).id !== (existing as any).id ||
                                       (item as any).firstName !== (existing as any).firstName ||
                                       (item as any).lastName !== (existing as any).lastName;
                            });
                        return isDifferent ? finalItems : prev;
                    });
                }
            } catch (err) {
                console.error(`[useFirestoreCollection] Re-transform error on ${collectionName}:`, err);
            }
        };

        retransform();

        return () => {
            isMounted = false;
        };
    }, [transform, collectionName, enabled, userId]);

    // Track active subscription
    useEffect(() => {
        if (!userId || !enabled) {
            setData(initialData);
            if (loading) setLoading(false);
            return;
        }

        let isMounted = true;
        const colRef = collection(db, 'users', userId, collectionName);
        const q = constraints.length > 0 
            ? query(colRef, ...constraints)
            : query(colRef);

        // console.log(`[Firestore] 📡 Subscribing to: ${collectionName}`);

        const unsubscribe = onSnapshot(q, async (snapshot) => {
            if (!isMounted) return;
            const rawItems: T[] = [];
            snapshot.forEach((doc) => {
                rawItems.push({ ...doc.data(), id: doc.id } as T);
            });
            
            rawItemsRef.current = rawItems;

            let finalItems = rawItems;
            if (transformRef.current) {
                try {
                    finalItems = await Promise.all(rawItems.map(item => transformRef.current!(item)));
                } catch (err) {
                    console.error(`[useFirestoreCollection] Transform error on ${collectionName}:`, err);
                }
            }

            if (isMounted) {
                setData(finalItems);
                setLoading(false);
            }
        }, (error) => {
            if (!isMounted) return;
            console.error(`[useFirestoreCollection] Error syncing ${collectionName}:`, error);
            setLoading(false);
        });

        return () => {
            isMounted = false;
            // console.log(`[Firestore] 🛑 Unsubscribing from: ${collectionName}`);
            unsubscribe();
        };
    }, [userId, collectionName, enabled, prevParams]);  

    return [data, loading];
};
